import { ref, onUnmounted, type Ref } from "vue";

const POLLING_INTERVAL_MS = 30_000;

interface FlagData {
  flag: Ref<boolean>;
  unleashReady: Ref<boolean>;
  hasSetupListeners: boolean;
  pollInterval: NodeJS.Timeout | null;
  listenerCount: number;
  cleanup: (() => void)[];
}

const flagCache = new Map<string, FlagData>();

export function useFlag(flagName: string): Ref<boolean> {
  let flagData = flagCache.get(flagName);

  if (!flagData) {
    flagData = {
      flag: ref(false),
      unleashReady: ref(false),
      hasSetupListeners: false,
      pollInterval: null,
      listenerCount: 0,
      cleanup: [],
    };
    flagCache.set(flagName, flagData);
  }

  const sharedFlag = flagData.flag;
  flagData.listenerCount++;

  const updateFlagValue = () => {
    const data = flagCache.get(flagName);
    if (!data || !isClient()) return;

    try {
      const unleashManager = useUnleashManager();
      const client = unleashManager?.useUnleashClient();

      if (client && typeof client.isEnabled === "function") {
        const newValue = client.isEnabled(flagName);
        if (data.flag.value !== newValue) {
          data.flag.value = newValue;
        }

        if (!data.unleashReady.value) {
          data.unleashReady.value = true;
        }
      }
    } catch (error) {
      return false;
    }
  };

  const startPolling = () => {
    const data = flagCache.get(flagName);
    if (!data || data.pollInterval || !isClient()) return;

    data.pollInterval = setInterval(updateFlagValue, POLLING_INTERVAL_MS);
  };

  const stopPolling = () => {
    const data = flagCache.get(flagName);
    if (data?.pollInterval) {
      clearInterval(data.pollInterval);
      data.pollInterval = null;
    }
  };

  const setupListeners = () => {
    const data = flagCache.get(flagName);
    if (!data || data.hasSetupListeners || !isClient()) return;

    try {
      const unleashManager = useUnleashManager();
      const client = unleashManager?.useUnleashClient();

      if (client && typeof client.on === "function") {
        data.hasSetupListeners = true;

        const onReady = () => updateFlagValue();
        const onUpdate = () => updateFlagValue();

        client.on("ready", onReady);
        client.on("update", onUpdate);

        data.cleanup.push(() => {
          client.off("ready", onReady);
          client.off("update", onUpdate);
        });

        updateFlagValue();
        startPolling();
      }
    } catch (error) {
      return false;
    }
  };

  if (isClient()) {
    onUnmounted(() => {
      const data = flagCache.get(flagName);
      if (data) {
        data.listenerCount--;

        if (data.listenerCount <= 0) {
          data.cleanup.forEach((cleanup) => cleanup());
          stopPolling();
          flagCache.delete(flagName);
        }
      }
    });
  }

  if (!flagData.hasSetupListeners) {
    updateFlagValue();
    setupListeners();

    if (!flagData.hasSetupListeners && isClient()) {
      setTimeout(setupListeners, 2000);
    }
  }

  return sharedFlag;
}

export function useFlagWithReady(flagName: string): {
  flag: Ref<boolean>;
  ready: Ref<boolean>;
} {
  const flag = useFlag(flagName);
  const flagData = flagCache.get(flagName);
  const ready = flagData?.unleashReady ?? ref(false);

  return { flag, ready };
}
